import { Head, router } from "@inertiajs/react"
import { Plus, Trash2, Route } from "lucide-react"
import { useMemo, useState } from "react"
import { AdminLayout } from "../components/AdminLayout"
import { SectionTabs } from "../components/SectionTabs"

const coursesTabs = [
  { label: "Courses", href: "/app/admin/courses" },
  { label: "Tracks", href: "/app/admin/learning_tracks" },
  { label: "Categories", href: "/app/admin/course_categories" },
]
import { TrackFormDialog } from "./TrackFormDialog"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableHeader } from "@/components/ui/sortable-header"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function StatusBadge({ status }) {
  if (status === "published") {
    return (
      <Badge className="bg-green-500 text-white hover:bg-green-500">
        Published
      </Badge>
    )
  }
  return <Badge variant="secondary">Draft</Badge>
}

const TRACK_STATUS_ORDER = { draft: 0, published: 1 }

function trackCompare(a, b, sortKey, direction) {
  let aVal
  let bVal
  if (sortKey === "title") {
    aVal = a.title?.toLowerCase() || null
    bVal = b.title?.toLowerCase() || null
  } else if (sortKey === "courses") {
    aVal = a.coursesCount ?? 0
    bVal = b.coursesCount ?? 0
  } else if (sortKey === "status") {
    aVal = a.status != null ? TRACK_STATUS_ORDER[a.status] : null
    bVal = b.status != null ? TRACK_STATUS_ORDER[b.status] : null
  }
  if (aVal === bVal) return 0
  if (aVal == null) return 1
  if (bVal == null) return -1
  const cmp = aVal < bVal ? -1 : 1
  return direction === "asc" ? cmp : -cmp
}

// ---------------------------------------------------------------------------
// Status Filter Tabs
// ---------------------------------------------------------------------------

function StatusFilter({ stats, currentStatus }) {
  const filters = [
    { label: "All", value: null, count: stats.total },
    { label: "Published", value: "published", count: stats.published },
    { label: "Drafts", value: "draft", count: stats.drafts },
  ]

  return (
    <div className="flex gap-1">
      {filters.map((f) => {
        const isActive = currentStatus === f.value
        return (
          <button
            key={f.label}
            onClick={() =>
              router.get(
                "/app/admin/learning_tracks",
                f.value ? { status: f.value } : {},
                { preserveState: true, preserveScroll: true }
              )
            }
            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
              isActive
                ? "bg-zinc-900 text-white"
                : "text-zinc-600 hover:bg-zinc-100"
            }`}
          >
            {f.label}
            <span className="ml-1.5 text-xs opacity-70">({f.count})</span>
          </button>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function LearningTracksIndex({
  currentUser,
  tracks,
  stats,
  filters,
  pendingCount,
}) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogKey, setDialogKey] = useState(0)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [sortKey, setSortKey] = useState("title")
  const [direction, setDirection] = useState("asc")

  const handleSort = (column) => {
    if (sortKey !== column) {
      setSortKey(column)
      setDirection("asc")
      return
    }
    if (direction === "asc") {
      setDirection("desc")
      return
    }
    setSortKey("title")
    setDirection("asc")
  }

  const sortedTracks = useMemo(() => {
    if (!tracks?.length) return []
    const arr = [...tracks]
    arr.sort((a, b) => trackCompare(a, b, sortKey, direction))
    return arr
  }, [tracks, sortKey, direction])

  const handleCreate = () => {
    setDialogKey((k) => k + 1)
    setDialogOpen(true)
  }

  const handleDelete = () => {
    if (deleteTarget) {
      router.delete(`/app/admin/learning_tracks/${deleteTarget.id}`, {
        preserveScroll: true,
        onSuccess: () => setDeleteTarget(null),
      })
    }
  }

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title="Learning Tracks - Admin - Kingdom Factor" />

      <div className="space-y-6">
        <SectionTabs tabs={coursesTabs} />

        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-zinc-900">
              Learning Tracks
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Bundle courses into ordered tracks for group assignment
            </p>
          </div>
          <Button variant="gradient" onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Create Track
          </Button>
        </div>

        <StatusFilter stats={stats} currentStatus={filters.currentStatus} />

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHeader
                  column="title"
                  sortKey={sortKey}
                  direction={direction}
                  onSort={handleSort}
                >
                  Title
                </SortableHeader>
                <SortableHeader
                  column="courses"
                  sortKey={sortKey}
                  direction={direction}
                  onSort={handleSort}
                  className="hidden md:table-cell w-24 text-center"
                >
                  Courses
                </SortableHeader>
                <SortableHeader
                  column="status"
                  sortKey={sortKey}
                  direction={direction}
                  onSort={handleSort}
                  className="hidden sm:table-cell w-28"
                >
                  Status
                </SortableHeader>
                <TableHead className="text-right w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTracks.map((track) => (
                <TableRow
                  key={track.id}
                  className="cursor-pointer hover:bg-zinc-50"
                  onClick={() =>
                    router.visit(`/app/admin/learning_tracks/${track.id}`)
                  }
                >
                  <TableCell className="whitespace-normal">
                    <div className="min-w-0">
                      <span className="font-medium text-zinc-900">
                        {track.title}
                      </span>
                      {track.description && (
                        <p className="text-sm text-zinc-500 line-clamp-1 mt-0.5">
                          {track.description}
                        </p>
                      )}
                      <div className="mt-1.5 flex items-center gap-2 sm:hidden">
                        <StatusBadge status={track.status} />
                        <span className="text-xs text-zinc-500">
                          {track.coursesCount} {track.coursesCount === 1 ? "course" : "courses"}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-center">
                    <span className="text-sm text-zinc-600">
                      {track.coursesCount}
                    </span>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <StatusBadge status={track.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeleteTarget(track)
                        }}
                        title="Delete track"
                        aria-label={`Delete ${track.title}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {sortedTracks.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center py-8 text-zinc-500"
                  >
                    <Route className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
                    <p className="text-sm">No learning tracks yet.</p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Click &ldquo;Create Track&rdquo; to bundle courses
                      together.
                    </p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      <TrackFormDialog
        key={dialogKey}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Learning Track</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.groupsCount > 0
                ? `This track is assigned to ${deleteTarget.groupsCount} group${deleteTarget.groupsCount === 1 ? "" : "s"}. Deleting it will remove access to its courses for members of those groups. This action cannot be undone.`
                : `Delete "${deleteTarget?.title}"? This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleDelete}
            >
              Delete Track
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  )
}
